import { FoodBrandInput } from '../components/FoodBrandInput';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { parseNumericInput, type Food } from '@jungles/shared';
import { api } from '../lib/api';
import { useApi } from '../lib/hooks';
import { useToast } from '../app/toast';
import { BackLink, ErrorState, Header, Loader, Screen, TabBar } from '../components/Chrome';

const nutrients = [
  ['kcal100', 'Калории, ккал', 10000],
  ['p100', 'Белки, г', 1000],
  ['f100', 'Жиры, г', 1000],
  ['c100', 'Углеводы, г', 1000],
] as const;
type Nutrient = typeof nutrients[number][0];
type Form = { name: string; brand: string; servingGrams: string } & Record<Nutrient, string>;

/** Catalog editor deliberately reads the current Food, never navigation prefill or diary. */
export function FoodEditScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const libraryPath = `/nutrition/library${location.search}`;
  const toast = useToast();
  const { data: food, error: loadError, loading, reload } = useApi<Food>(`/api/foods/${encodeURIComponent(id)}`);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (food?.id !== id || loading || loadError) return;
    setForm({ servingGrams: food.servingGrams == null ? '' : String(food.servingGrams), name: food.name, brand: food.brand ?? '', kcal100: String(food.kcal100), p100: String(food.p100), f100: String(food.f100), c100: String(food.c100) });
  }, [food, id, loading, loadError]);
  const editable = food?.id === id && food.isOwn && ['user', 'ocr', 'community'].includes(food.source);
  const valid = Boolean(form && form.name.trim().length > 0 && form.name.trim().length <= 120 && form.brand.trim().length <= 80 && (!form.servingGrams.trim() || Number.isFinite(parseNumericInput(form.servingGrams)) && parseNumericInput(form.servingGrams) > 0 && parseNumericInput(form.servingGrams) <= 5000) && nutrients.every(([key, , max]) => {
    const value = parseNumericInput(form[key]);
    return Number.isFinite(value) && value >= 0 && value <= max;
  }));
  const save = async () => {
    if (!form || !editable || !valid || busy || loading || loadError) return;
    setBusy(true); setError(null);
    try {
      await api.patch(`/api/foods/${encodeURIComponent(id)}`, {
        servingGrams: form.servingGrams.trim() ? parseNumericInput(form.servingGrams) : null,
        name: form.name.trim(), brand: form.brand.trim() || null,
        ...Object.fromEntries(nutrients.map(([key]) => [key, parseNumericInput(form[key])])),
      });
      toast('Продукт сохранён');
      navigate(libraryPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
      setBusy(false);
    }
  };
  return <Screen tabs>
    <Header />
    <BackLink to={libraryPath} label="Мои продукты" />
    <h1>Изменить продукт</h1>
    {loading ? <Loader /> : loadError ? <ErrorState message={loadError} onRetry={reload} /> : !editable ? <p className="err" role="alert">Этот продукт нельзя редактировать</p> : form ? <section className="card">
      <h2>Продукт и КБЖУ</h2>
      <label className="fl">Название<input className="f" value={form.name} maxLength={120} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
      <FoodBrandInput value={form.brand} onChange={brand => setForm({...form,brand})} />
      {food.barcode ? <p className="nt">Штрихкод: <span className="code">{food.barcode}</span></p> : null}
      <label className="fl">Стандартная порция, г <span className="mu">· необязательно</span><input className="f" aria-label="Стандартная порция, г" inputMode="decimal" value={form.servingGrams} onChange={event => setForm({...form, servingGrams: event.target.value})} /></label>
      <p className="nt">КБЖУ на 100 г</p>
      <div className="fields">{nutrients.map(([key, label]) => <label className="fl" key={key}>{label}<input className="f" type="text" inputMode="decimal" value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} /></label>)}</div>
      {error ? <p className="err" role="alert">{error}</p> : null}
      <div className="acts">
        <button className="b p big" type="button" disabled={!valid || busy} onClick={() => void save()}>Сохранить</button>
        <button className="b" type="button" disabled={busy} onClick={() => navigate(libraryPath)}>Отмена</button>
      </div>
    </section> : null}
    <TabBar />
  </Screen>;
}
